// Store/Actions.js
import { 
  getConfigData, 
  getMasterDataFromConfig, 
  addMasterDataItem, 
  removeMasterDataItem,
  getConnFieldPrefsFromConfig,
  getStorageData,
  setStorageData
} from '../Utils/Storage';
import { saveConnectionApi } from '../Modules/Connections/ConnectionAPI';
import { addThreadApi, updateThreadApi, deleteThreadApi } from '../Modules/Threads/ThreadsAPI';
import { getFromIndexedDB } from '../Utils/IndexedDBUtils';
import { getAuthData } from '../Utils/AuthUtils';
import { getLastSyncDT } from '../Utils/APIConfig';
import apiService from '../Services/APIService';
import { getThreadFieldPrefsFromConfig } from './Reducers';

// Action type constants for master data operations
export const FETCH_MASTER_DATA_REQUEST = 'FETCH_MASTER_DATA_REQUEST';
export const FETCH_MASTER_DATA_SUCCESS = 'FETCH_MASTER_DATA_SUCCESS';
export const FETCH_MASTER_DATA_FAILURE = 'FETCH_MASTER_DATA_FAILURE';

export const ADD_MASTER_ITEM_REQUEST = 'ADD_MASTER_ITEM_REQUEST';
export const ADD_MASTER_ITEM_SUCCESS = 'ADD_MASTER_ITEM_SUCCESS';
export const ADD_MASTER_ITEM_FAILURE = 'ADD_MASTER_ITEM_FAILURE';

export const DELETE_MASTER_ITEM_REQUEST = 'DELETE_MASTER_ITEM_REQUEST';
export const DELETE_MASTER_ITEM_SUCCESS = 'DELETE_MASTER_ITEM_SUCCESS';
export const DELETE_MASTER_ITEM_FAILURE = 'DELETE_MASTER_ITEM_FAILURE';

export const ADD_PRODUCT_REQUEST = 'ADD_PRODUCT_REQUEST';
export const ADD_PRODUCT_SUCCESS = 'ADD_PRODUCT_SUCCESS';
export const ADD_PRODUCT_FAILURE = 'ADD_PRODUCT_FAILURE';

export const DELETE_PRODUCT_REQUEST = 'DELETE_PRODUCT_REQUEST';
export const DELETE_PRODUCT_SUCCESS = 'DELETE_PRODUCT_SUCCESS';
export const DELETE_PRODUCT_FAILURE = 'DELETE_PRODUCT_FAILURE';

export const FETCH_PRODUCTS_REQUEST = 'FETCH_PRODUCTS_REQUEST';
export const FETCH_PRODUCTS_SUCCESS = 'FETCH_PRODUCTS_SUCCESS';
export const FETCH_PRODUCTS_FAILURE = 'FETCH_PRODUCTS_FAILURE';

export const REFRESH_ALL_MASTER_DATA = 'REFRESH_ALL_MASTER_DATA';
export const FETCH_ACTION_DATA_SUCCESS = 'FETCH_ACTION_DATA_SUCCESS';

export const SAVE_CONNECTION_REQUEST = 'SAVE_CONNECTION_REQUEST';
export const SAVE_CONNECTION_SUCCESS = 'SAVE_CONNECTION_SUCCESS';
export const SAVE_CONNECTION_FAILURE = 'SAVE_CONNECTION_FAILURE';

export const FETCH_CONNECTIONS_REQUEST = 'FETCH_CONNECTIONS_REQUEST';
export const FETCH_CONNECTIONS_SUCCESS = 'FETCH_CONNECTIONS_SUCCESS';
export const FETCH_CONNECTIONS_FAILURE = 'FETCH_CONNECTIONS_FAILURE';

// Connection Field Preferences Action Types
export const UPDATE_CONN_FIELD_PREF_REQUEST = 'UPDATE_CONN_FIELD_PREF_REQUEST';
export const UPDATE_CONN_FIELD_PREF_SUCCESS = 'UPDATE_CONN_FIELD_PREF_SUCCESS';
export const UPDATE_CONN_FIELD_PREF_FAILURE = 'UPDATE_CONN_FIELD_PREF_FAILURE';

// Thread Field Preferences Action Types
export const UPDATE_THREAD_FIELD_PREFS_REQUEST = 'UPDATE_THREAD_FIELD_PREFS_REQUEST';
export const UPDATE_THREAD_FIELD_PREFS_SUCCESS = 'UPDATE_THREAD_FIELD_PREFS_SUCCESS';
export const UPDATE_THREAD_FIELD_PREFS_FAILURE = 'UPDATE_THREAD_FIELD_PREFS_FAILURE';

export const RELOAD_THREAD_FIELD_PREFS = 'RELOAD_THREAD_FIELD_PREFS';

// Thread/Action Management Action Types
export const SAVE_THREAD_REQUEST = 'SAVE_THREAD_REQUEST';
export const SAVE_THREAD_SUCCESS = 'SAVE_THREAD_SUCCESS';
export const SAVE_THREAD_FAILURE = 'SAVE_THREAD_FAILURE';

export const UPDATE_THREAD_REQUEST = 'UPDATE_THREAD_REQUEST';
export const UPDATE_THREAD_SUCCESS = 'UPDATE_THREAD_SUCCESS';
export const UPDATE_THREAD_FAILURE = 'UPDATE_THREAD_FAILURE';

export const DELETE_THREAD_REQUEST = 'DELETE_THREAD_REQUEST';
export const DELETE_THREAD_SUCCESS = 'DELETE_THREAD_SUCCESS';
export const DELETE_THREAD_FAILURE = 'DELETE_THREAD_FAILURE';

export const FETCH_THREADS_REQUEST = 'FETCH_THREADS_REQUEST';
export const FETCH_THREADS_SUCCESS = 'FETCH_THREADS_SUCCESS';
export const FETCH_THREADS_FAILURE = 'FETCH_THREADS_FAILURE';

// Users Action Types
export const FETCH_USERS_REQUEST = 'FETCH_USERS_REQUEST';
export const FETCH_USERS_SUCCESS = 'FETCH_USERS_SUCCESS';
export const FETCH_USERS_FAILURE = 'FETCH_USERS_FAILURE';
export const UPDATE_USER_SUCCESS = 'UPDATE_USER_SUCCESS';
export const ADD_USER_SUCCESS = 'ADD_USER_SUCCESS';
export const REMOVE_USER_SUCCESS = 'REMOVE_USER_SUCCESS';

export const RELOAD_ALL_THREAD_FIELD_PREFS = 'RELOAD_ALL_THREAD_FIELD_PREFS';

// Global Loader Type
export const SET_GLOBAL_LOADING = 'SET_GLOBAL_LOADING';

// Map redux state keys to localStorage storage keys
const MASTER_TYPE_MAPPING = {
  sources: 'Sources',
  cities: 'Cities',
  extraFields: 'ConnFields',
  rejectionReasons: 'RejRsns',
  disqlReasons: 'DisqlRsns',  
  inactiveReasons: 'InactRsns',
  segments: 'Segments',
  appointmentTypes: 'TaskTypes',
  products: 'Products',
  recordTypes: 'RecTypes'
};

// ==================== GLOBAL LOADER ACTION ====================
export const setGlobalLoading = (isLoading) => ({
  type: SET_GLOBAL_LOADING,
  payload: isLoading
})

// ==================== THREAD FIELD PREFERENCES API FUNCTIONS ====================

// Update thread field preferences API function
export const updateThreadFieldPrefsApi = async (preferences, actionType) => {
  try {
    const authData = getAuthData();
    if (!authData) {
      throw new Error('Authentication data not found');
    }

    // Map action types to API keys
    const actionTypeMap = {
      'inquiry': 'INQ',
      'order': 'ORD', 
      'support': 'SUP'
    };

    const apiActionType = actionTypeMap[actionType];
    if (!apiActionType) {
      throw new Error(`Invalid action type: ${actionType}`);
    }

    // Convert preferences to API format
    const apiFieldPrefs = {
      POC: preferences.Contact || false,
      Item: preferences.Model || false,
      Ref: preferences.Reference || false,
      Qty: preferences.Quantity || false,
      AsgnTo: preferences.AssignedTo || false,
      NextTsk: preferences.NextAction || false
    };

    // Create the API payload matching your exact format
    const configData = [{
      changeType: "ENTRYPREFS",
      key: apiActionType,
      value: "",
      values: JSON.stringify([apiFieldPrefs])
    }];

    const lastSyncDT = getLastSyncDT() || new Date().toISOString();

    const parameters = [{
      UserID: authData.UserID,
      Token: authData.WebToken,
      ForAgencyID: authData.AgencyID,
      LastSyncDT: lastSyncDT,
      IsWeb: true,
      ModuleID: 3,
      ActionType: "set",
      Data: JSON.stringify(configData)
    }];

    const result = await apiService.sendChange(parameters);
    return result;

  } catch (error) {
    throw error;
  }
};

// ==================== THREAD FIELD PREFERENCES ACTIONS ====================

// Update thread field preferences with API sync
export const updateThreadFieldPrefsWithApi = (preferences, actionType) => async (dispatch) => {
  dispatch({ type: UPDATE_THREAD_FIELD_PREFS_REQUEST });
  
  try {
    // Update local state immediately for better UX
    dispatch({
      type: 'UPDATE_ALL_THREAD_FIELD_PREFS',
      payload: { actionType, preferences }
    });

    // Send to API in background
    const result = await updateThreadFieldPrefsApi(preferences, actionType);    
    dispatch({ type: UPDATE_THREAD_FIELD_PREFS_SUCCESS });
    
    // After successful API call, reload from localStorage to ensure Redux state 
    // matches what the API response might have updated in localStorage
    setTimeout(() => {
      dispatch(reloadThreadFieldPrefs(actionType));
    }, 100);
    
    return result;
    
  } catch (error) {    
    // Keep local changes even if API fails for better UX
    dispatch({ 
      type: UPDATE_THREAD_FIELD_PREFS_FAILURE, 
      payload: error.message 
    });
    
    // Still throw error so component can handle it
    throw error;
  }
};

export const reloadThreadFieldPrefs = (actionType) => ({
  type: RELOAD_THREAD_FIELD_PREFS,
  payload: { actionType }
});


// ==================== CONNECTION FIELD PREFERENCES API FUNCTION ====================

// Add connection field preferences API function
export const updateConnFieldPrefsApi = async (connFieldPrefs) => {
  try {
    const authData = getAuthData();
    if (!authData) {
      throw new Error('Authentication data not found');
    }

    // Create the API payload matching your exact format
    const configData = [{
      changeType: "ENTRYPREFS",
      key: "CONN", 
      value: "",
      values: JSON.stringify([connFieldPrefs])
    }];

    const lastSyncDT = getLastSyncDT() || "2025-06-28T14:09:00";

    const parameters = [{
      UserID: authData.UserID,
      Token: authData.WebToken,
      ForAgencyID: authData.AgencyID,
      LastSyncDT: lastSyncDT,
      IsWeb: true,
      ModuleID: 3,
      ActionType: "set",
      Data: JSON.stringify(configData)
    }];

    const result = await apiService.sendChange(parameters);
    return result;

  } catch (error) {
    throw error;
  }
};


// ==================== USERS ACTIONS ====================

export const fetchUsers = () => async (dispatch) => {
  dispatch({ type: FETCH_USERS_REQUEST });
  try {
    const usersData = await getStorageData('U') || [];
    const activeUsers = usersData.filter(user => user.IsRemove === 0);
    dispatch({ type: FETCH_USERS_SUCCESS, payload: activeUsers });
  } catch (error) {
    dispatch({ type: FETCH_USERS_FAILURE, payload: error.message });
  }
};

export const updateUser = (userData) => async (dispatch) => {
  dispatch({ type: FETCH_USERS_REQUEST });
  try {
    const currentUsers = await getStorageData('U') || [];
    const updatedUsers = currentUsers.map(user =>
      user.UserID === userData.UserID ? { ...user, ...userData } : user
    );
    setStorageData('U', updatedUsers);
    dispatch({ type: UPDATE_USER_SUCCESS, payload: userData });
  } catch (error) {
    dispatch({ type: FETCH_USERS_FAILURE, payload: error.message });
  }
};

export const addUser = (userData) => async (dispatch) => {
  dispatch({ type: FETCH_USERS_REQUEST });
  try {
    const currentUsers = await getStorageData('U') || [];
    const newUser = {
      ...userData,
      UserID: Math.max(...currentUsers.map(u => u.UserID || 0), 0) + 1,
      NodeID: userData.NodeID || Math.max(...currentUsers.map(u => u.NodeID || 0), 0) + 1,
      IsRemove: 0,
      Tbl: 'U'
    };
    const updatedUsers = [...currentUsers, newUser];
    setStorageData('U', updatedUsers);
    dispatch({ type: ADD_USER_SUCCESS, payload: newUser });
  } catch (error) {
    dispatch({ type: FETCH_USERS_FAILURE, payload: error.message });
  }
};

export const removeUser = (userId) => async (dispatch) => {
  dispatch({ type: FETCH_USERS_REQUEST });
  try {
    const currentUsers = await getStorageData('U') || [];
    const updatedUsers = currentUsers.map(user =>
      user.UserID === userId ? { ...user, IsRemove: 1 } : user
    );
    setStorageData('U', updatedUsers);
    dispatch({ type: REMOVE_USER_SUCCESS, payload: userId });
  } catch (error) {
    dispatch({ type: FETCH_USERS_FAILURE, payload: error.message });
  }
};

// ==================== MASTER DATA ACTIONS ====================

// Fetch master data from localStorage for specified type
export const fetchMasterData = (masterType) => (dispatch) => {
  dispatch({ type: FETCH_MASTER_DATA_REQUEST });
  try {
    const storageKey = MASTER_TYPE_MAPPING[masterType];
    const data = getMasterDataFromConfig(storageKey);
    
    dispatch({ 
      type: FETCH_MASTER_DATA_SUCCESS, 
      payload: { type: masterType, data } 
    });
  } catch (error) {
    dispatch({ type: FETCH_MASTER_DATA_FAILURE, payload: error.message });
  }
};

// Fetch active products from localStorage with filtering
export const fetchProducts = () => (dispatch) => {
  dispatch({ type: FETCH_PRODUCTS_REQUEST });
  try {
    const products = getMasterDataFromConfig('Products');
    
    const activeProducts = products.filter(product => 
      product.IsD === false || product.IsD === 0
    );
    
    dispatch({ type: FETCH_PRODUCTS_SUCCESS, payload: activeProducts });
  } catch (error) {
    dispatch({ type: FETCH_PRODUCTS_FAILURE, payload: error.message });
  }
};

// Add master data item with optimistic updates and background API sync
export const addMasterItem = (masterType, itemName) => async (dispatch) => {
  dispatch({ type: ADD_MASTER_ITEM_REQUEST });
  try {
    // Map the masterType to the correct API key format
    const masterTypeMapping = {
      city: 'cities',
      source: 'sources',
      segment: 'segments',
      nextActionType: 'appointmentTypes',
    };
    const normalizedMasterType = masterTypeMapping[masterType] || masterType;

    const storageKey = MASTER_TYPE_MAPPING[normalizedMasterType];
    
    const updatedData = addMasterDataItem(storageKey, itemName);
    
    dispatch({ 
      type: ADD_MASTER_ITEM_SUCCESS, 
      payload: { type: normalizedMasterType, data: updatedData } 
    });
    
    
  } catch (error) {
    dispatch({ type: ADD_MASTER_ITEM_FAILURE, payload: error.message });
    throw error;
  }
};

// Get item identifier for deletion based on master data type
const getItemIdForDeletion = (masterType, index, data, storageKey) => {
  if (masterType === 'extraFields') {
    const configData = getConfigData();
    const fullConnFields = JSON.parse(configData.ConnFields || '[]');
    const displayData = getMasterDataFromConfig(storageKey);
    const fieldName = displayData[index];
    const field = fullConnFields.find(f => f.F === fieldName && (f.IsD === 0 || f.IsD === false));
    return field ? field.FID : null;
  }
  
  return data[index];
};



// Refresh all master data from localStorage to sync Redux state
export const refreshAllMasterData = () => (dispatch) => {
  // Actually fetch fresh data for all master types
  Object.keys(MASTER_TYPE_MAPPING).forEach(masterType => {
    if (masterType === 'products') {
      dispatch(fetchProducts());
    } else {
      dispatch(fetchMasterData(masterType));
    }
  });
};

// Initialize all master data on application startup
export const initializeMasterData = () => (dispatch) => {
  Object.keys(MASTER_TYPE_MAPPING).forEach(masterType => {
    if (masterType === 'products') {
      dispatch(fetchProducts());
    } else {
      dispatch(fetchMasterData(masterType));
    }
  });
};